import React, { useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { GRADES, type WarehouseRecord } from "@/types/inventory";
import type { DispatchRecord } from "@/types";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

/** Goods that came back from a delivered dispatch: how much of each roll, into which warehouse and grade */
export function ReturnDialog({ dispatch, open, onClose, onSaved }: { dispatch: DispatchRecord | null; open: boolean; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [warehouseId, setWarehouseId] = useState("");
  const [grade, setGrade] = useState("SECOND");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty({});
    setWarehouseId("");
    setGrade("SECOND");
    setReason("");
    api.get("/api/Warehouse/List").then((r) => setWarehouses(unwrapArray<WarehouseRecord>(r))).catch(() => setWarehouses([]));
  }, [open, dispatch?.id]);

  // Only rolls taken from stock that are still out with the buyer can be returned
  const rolls = (dispatch?.lines || []).flatMap((l) => (l.rolls || []).filter((r) => r.stock_unit_id && r.stock_status === "DISPATCHED"));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatch) return;
    const items = rolls.filter((r) => Number(qty[r.stock_unit_id!]) > 0).map((r) => ({ unit_id: r.stock_unit_id!, quantity: Number(qty[r.stock_unit_id!]) }));
    if (!items.length) return error("Validation Error", "Enter how much came back for at least one roll");
    if (!reason.trim()) return error("Validation Error", "Say why the goods came back");
    setSaving(true);
    try {
      const r = unwrapObject<{ return_number: string }>(await api.post(`/api/SalesOrder/Return/${dispatch.id}`, { items, warehouse_id: warehouseId || undefined, grade, reason: reason.trim() }));
      success("Return recorded", `${r.return_number}: ${items.length} roll${items.length === 1 ? "" : "s"} back in stock`);
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not record the return", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Goods returned from {dispatch?.dispatch_number}</DialogTitle>
          <DialogDescription>Enter what came back of each roll. It goes into stock in the grade and warehouse you choose.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="space-y-4 py-2 text-sm overflow-y-auto max-h-[60vh] pr-1">
            {rolls.length === 0 ? (
              <p className="text-muted-foreground">No roll of this dispatch was taken from stock, or all of them have already been returned.</p>
            ) : (
              <div className="rounded-lg border border-border divide-y divide-border">
                {rolls.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">Roll {r.roll_no}</div>
                      <div className="text-muted-foreground">Lot {r.lot_number || "-"} · sent {Number(r.quantity || 0)}</div>
                    </div>
                    <Input className="h-8 w-32 text-xs" inputMode="decimal" placeholder="Came back" value={qty[r.stock_unit_id!] || ""} onChange={(e) => setQty({ ...qty, [r.stock_unit_id!]: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Back into</Label>
                <select className={field} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                  <option value="">The roll's own warehouse</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Grade</Label>
                <select className={field} value={grade} onChange={(e) => setGrade(e.target.value)}>
                  {Object.entries(GRADES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reason *</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Shade off, wrong width, buyer cancelled…" />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || rolls.length === 0} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Take back into stock
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
